import { useMemo } from "react";
import { useProject } from "@/hooks/use-project";
import type { ProjectUser } from "@/types/api";

/**
 * Everyone on a project who can be assigned an issue, or filtered by.
 *
 * The owner is included and listed first. They have no `ProjectMember` row —
 * ownership is a column on the project, not a membership — so a list built from
 * `members` alone silently omits the one person who is always on the project.
 * The backend accepts them as an assignee, so the picker must offer them.
 *
 * Deduped by id: an owner who also holds a membership row would otherwise
 * appear twice.
 */
export function useProjectPeople(projectId: string): {
  people: ProjectUser[];
  isLoading: boolean;
} {
  const { data: project, isLoading } = useProject(projectId);

  const people = useMemo(() => {
    if (!project) return [];
    const seen = new Set<string>();
    const out: ProjectUser[] = [];

    const push = (person: ProjectUser | null | undefined) => {
      if (!person || seen.has(person.id)) return;
      seen.add(person.id);
      out.push(person);
    };

    push(project.owner);
    for (const member of project.members) push(member.user);
    return out;
  }, [project]);

  return { people, isLoading };
}
